import { HUMAN_REVIEW_STATUS } from "@/ai-coding-outputs/constants";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";

export type RecentReviewedEncounter = {
  encounterId: string;
  label: string;
};

const QUERY_KEY = "recent-reviewed-encounters";

export function useRecentReviewedEncounters(options?: { excludeEncounterId?: string }) {
  const excludeEncounterId = options?.excludeEncounterId;

  const {
    data: rawData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY, excludeEncounterId],
    queryFn: async (): Promise<RecentReviewedEncounter[]> => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return [];

      const { data: rows, error: queryError } = await supabase
        .from("encounter_human_review")
        .select("encounter_id, updated_at")
        .eq("reviewer_id", user.id)
        .eq("status", HUMAN_REVIEW_STATUS.COMPLETE)
        .eq("is_voided", false)
        .order("updated_at", { ascending: false })
        .limit(10);

      if (queryError) throw queryError;

      const items: RecentReviewedEncounter[] = (rows ?? []).map((row) => ({
        encounterId: row.encounter_id,
        label: `Reviewed at ${format(new Date(row.updated_at), "HH:mm")}`,
      }));

      if (excludeEncounterId) {
        return items.filter((item) => item.encounterId !== excludeEncounterId);
      }
      return items;
    },
    staleTime: 60 * 1000,
  });

  return {
    data: rawData ?? [],
    isLoading,
    error: error ?? null,
  };
}
